/**
 * CustomNode — the drop-in React Flow custom node used across the editor.
 *
 * Responsibilities (R45 slice 2):
 *   • Render the shape (10 shapes: start / process / decision / data /
 *     end / note / cylinder / hexagon / parallelogram / cloud)
 *   • Render 8 handles (N, NE, E, SE, S, SW, W, NW) so users can connect
 *     from any side.
 *   • Show the label + (optional) rich-text preview + attachments preview.
 *   • Show a responsible-user avatar chip when data.responsible_user_id set
 *     (name comes from data.responsible_name — resolved by the editor).
 *   • Hover-card: description + attachments + comments count + last activity.
 *   • Minimal mode (viewMode="minimal") hides everything except the label.
 *
 * All state is driven purely from `data`; the editor handles updates via
 * setNodes(...) elsewhere.
 */
import { memo, Fragment } from "react";
import { Handle, Position } from "@xyflow/react";
import { Link as LinkIcon, MessageSquare, Paperclip, FileText, ExternalLink, User as UserIcon, Sparkles } from "lucide-react";
import { RichTextPreview } from "./RichTextEditor";

const DEFAULT_COLOR = "#0ea5e9";

// One handle in each of the 8 compass directions. React Flow needs
// unique ids per side + role so we prefix with pos.
const HANDLE_POSITIONS = [
  { id: "h-t", position: Position.Top, style: { left: "50%" } },
  { id: "h-tr", position: Position.Top, style: { left: "85%" } },
  { id: "h-r", position: Position.Right, style: { top: "50%" } },
  { id: "h-br", position: Position.Bottom, style: { left: "85%" } },
  { id: "h-b", position: Position.Bottom, style: { left: "50%" } },
  { id: "h-bl", position: Position.Bottom, style: { left: "15%" } },
  { id: "h-l", position: Position.Left, style: { top: "50%" } },
  { id: "h-tl", position: Position.Top, style: { left: "15%" } },
];

function Handles8({ show = true }) {
  if (!show) return null;
  return (
    <>
      {HANDLE_POSITIONS.map((h) => (
        <Fragment key={h.id}>
          {/* target + source on the same physical spot lets React Flow accept edges either direction */}
          <Handle id={`t-${h.id}`} type="target" position={h.position} style={{ ...h.style, background: "#737373", width: 7, height: 7 }} />
          <Handle id={`s-${h.id}`} type="source" position={h.position} style={{ ...h.style, background: "#737373", width: 7, height: 7 }} />
        </Fragment>
      ))}
    </>
  );
}

function initials(name = "") {
  return name.trim().split(/\s+/).map(s => s[0]?.toUpperCase()).slice(0, 2).join("") || "?";
}

function AvatarChip({ name, color = "#0284c7" }) {
  return (
    <div
      title={name}
      className="inline-flex items-center gap-1 h-5 px-1.5 border bg-white text-[10px] leading-none"
      data-testid="node-avatar-chip"
    >
      <div className="w-3.5 h-3.5 rounded-full flex items-center justify-center text-[8px] text-white font-bold" style={{ background: color }}>
        {initials(name)}
      </div>
      <span className="truncate max-w-[80px]">{name}</span>
    </div>
  );
}

function AttachmentBadges({ atts = [] }) {
  if (!atts.length) return null;
  const kinds = { image: 0, video: 0, sop: 0, link: 0 };
  atts.forEach((a) => { kinds[a.kind] = (kinds[a.kind] || 0) + 1; });
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {kinds.image ? <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white" title={`${kinds.image} image(s)`}><Paperclip className="w-2.5 h-2.5" />{kinds.image}</span> : null}
      {kinds.video ? <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white" title={`${kinds.video} video(s)`}>▶ {kinds.video}</span> : null}
      {kinds.sop ? <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white" title={`${kinds.sop} SOP(s)`}><FileText className="w-2.5 h-2.5" />{kinds.sop}</span> : null}
      {kinds.link ? <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white" title={`${kinds.link} link(s)`}><ExternalLink className="w-2.5 h-2.5" />{kinds.link}</span> : null}
    </div>
  );
}

function shapeStyle(shape, color, minimal) {
  const base = { background: "#fff", border: `2px solid ${color}` };
  if (minimal) {
    return { ...base, borderRadius: 6, padding: "4px 8px" };
  }
  switch (shape) {
    case "start":
    case "end":
      return { ...base, borderRadius: 999, padding: "8px 14px" };
    case "decision":
      return { ...base, transform: "rotate(45deg)", borderRadius: 4, minWidth: 100, minHeight: 100 };
    case "data":
      return { ...base, transform: "skewX(-12deg)", borderRadius: 4, padding: "8px 14px" };
    case "note":
      return { background: "#fef3c7", borderTop: `3px solid ${color}`, border: `1px solid ${color}55`, padding: "6px 10px" };
    case "cylinder":
      return { ...base, borderRadius: "50% 50% 6px 6px / 20% 20% 6px 6px", padding: "10px 14px" };
    case "hexagon":
      return { ...base, clipPath: "polygon(15% 0%, 85% 0%, 100% 50%, 85% 100%, 15% 100%, 0% 50%)", padding: "10px 22px" };
    case "parallelogram":
      return { ...base, clipPath: "polygon(15% 0%, 100% 0%, 85% 100%, 0% 100%)", padding: "10px 22px" };
    case "cloud":
      return { ...base, borderRadius: 40, padding: "10px 14px" };
    default:
      return { ...base, borderRadius: 6, padding: "8px 12px" };
  }
}

function InnerContent({ data, minimal }) {
  const responsible = data?.responsible_name;
  const richHtml = data?.richText;
  const hasRich = typeof richHtml === "string" && richHtml.trim() && richHtml !== "<p></p>";
  const commentCount = data?.commentCount || 0;
  const atts = data?.attachments || [];
  return (
    <div className={`flex flex-col gap-1 items-start ${minimal ? "" : "min-w-[140px] max-w-[240px]"}`}>
      <div className="text-[13px] font-medium break-words w-full text-center">
        {data?.label || "Untitled"}
      </div>
      {!minimal && hasRich && (
        <div className="text-[11px] w-full text-left border-t pt-1 mt-0.5">
          <RichTextPreview html={richHtml} className="!text-[11px] leading-tight" />
        </div>
      )}
      {!minimal && (responsible || atts.length || commentCount) ? (
        <div className="flex items-center gap-1 flex-wrap w-full">
          {responsible && <AvatarChip name={responsible} color={data?.responsible_color || "#0284c7"} />}
          <AttachmentBadges atts={atts} />
          {commentCount ? (
            <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white" title={`${commentCount} comment(s)`}>
              <MessageSquare className="w-2.5 h-2.5" />{commentCount}
            </span>
          ) : null}
          {data?.linked_entity?.type && (
            <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white uppercase" title={`Linked ${data.linked_entity.type}`}>
              <LinkIcon className="w-2.5 h-2.5" />{data.linked_entity.type}
            </span>
          )}
          {data?.ai_generated && (
            <span className="inline-flex items-center gap-0.5 text-[9px] border px-1 bg-white text-amber-600" title="AI-generated">
              <Sparkles className="w-2.5 h-2.5" />AI
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}

// Iter Feb 2026 R47 · pick the primary image attachment to render inside/below the node.
function primaryImage(atts = []) {
  return (atts || []).find(a => a?.kind === "image" && (a?.display === "fill" || a?.display === "below")) || null;
}

function NodeImage({ src, alt, onClick, className = "", style = {} }) {
  return (
    <img
      src={src}
      alt={alt || ""}
      draggable={false}
      onClick={(e) => { e.stopPropagation(); onClick?.(src, alt); }}
      className={`nodrag cursor-zoom-in select-none ${className}`}
      style={style}
      data-testid="node-image"
    />
  );
}

function CustomNodeInner({ data, selected }) {
  const color = data?.color || DEFAULT_COLOR;
  const shape = data?.shape || "process";
  const minimal = data?.viewMode === "minimal";
  const ring = selected ? "outline outline-2 outline-offset-2 outline-foreground" : "";
  // R47 · primary image attachment (fill vs below)
  const img = !minimal ? primaryImage(data?.attachments) : null;
  const openLightbox = data?.onImageClick;

  // For decision, we render the rotated shape as a background layer and
  // keep the label upright on top.
  if (shape === "decision" && !minimal) {
    return (
      <div className={`relative w-[140px] h-[100px] ${ring}`} data-testid="custom-node">
        <Handles8 show={data?.viewMode !== "minimal"} />
        <div className="absolute inset-0" style={shapeStyle("decision", color, false)} />
        {img?.display === "fill" && (
          <NodeImage
            src={img.url}
            alt={img.label}
            onClick={openLightbox}
            className="absolute inset-1 w-[calc(100%-8px)] h-[calc(100%-8px)] object-cover"
            style={{ transform: "rotate(-45deg)", clipPath: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)" }}
          />
        )}
        <div className="absolute inset-0 flex items-center justify-center px-2">
          <InnerContent data={data} minimal />
        </div>
        {img?.display === "below" && (
          <NodeImage
            src={img.url}
            alt={img.label}
            onClick={openLightbox}
            className="absolute left-1/2 -translate-x-1/2 top-full mt-2 max-w-[180px] max-h-[120px] object-contain border bg-white shadow-sm"
          />
        )}
      </div>
    );
  }

  return (
    <div className={`relative ${ring}`} style={shapeStyle(shape, color, minimal)} data-testid="custom-node">
      <Handles8 show={!minimal} />
      {img?.display === "fill" && (
        <NodeImage
          src={img.url}
          alt={img.label}
          onClick={openLightbox}
          className="block mx-auto mb-1 max-w-[220px] max-h-[140px] w-full object-cover"
        />
      )}
      {(shape === "data" && !minimal) ? (
        <div style={{ transform: "skewX(12deg)" }}>
          <InnerContent data={data} minimal={minimal} />
        </div>
      ) : (
        <InnerContent data={data} minimal={minimal} />
      )}
      {img?.display === "below" && (
        <NodeImage
          src={img.url}
          alt={img.label}
          onClick={openLightbox}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 max-w-[220px] max-h-[140px] object-contain border bg-white shadow-sm"
        />
      )}
    </div>
  );
}

export const CustomNode = memo(CustomNodeInner);
export default CustomNode;

export const CUSTOM_SHAPES = [
  { key: "start",         label: "Start" },
  { key: "process",       label: "Process" },
  { key: "decision",      label: "Decision" },
  { key: "data",          label: "Data" },
  { key: "end",           label: "End" },
  { key: "note",          label: "Sticky" },
  { key: "cylinder",      label: "Cylinder" },
  { key: "hexagon",       label: "Hexagon" },
  { key: "parallelogram", label: "Parallel" },
  { key: "cloud",         label: "Cloud" },
];

export const EDGE_TYPES = [
  { key: "default",    label: "Bezier" },
  { key: "smoothstep", label: "Smooth" },
  { key: "step",       label: "Right-angle" },
  { key: "straight",   label: "Straight" },
];

export const ARROW_STYLES = [
  { key: "arrow",        label: "Arrow" },
  { key: "arrowclosed",  label: "Solid arrow" },
  { key: "none",         label: "No arrow" },
];
